/**
 * Análise determinística do Relatório Enterprise.
 * Lê os mesmos campos do motor do mapa (lt + tpUnit, tt) e não trata ausência como zero.
 * O renderer do PDF não recalcula estas regras.
 */

import { convertLtToMinutes } from '../../utils/calculations';
import { analyzeFlow, evolutionRows, formatExecutiveDuration, presentOwners, scenarioIsComparable, structuralChanges } from './enterpriseFlowAnalysis';

export interface EnterpriseCardInput {
  id: string;
  tipo?: string;
  status?: string;
  atividade?: string;
  cliente?: string;
  responsavel?: string;
  cargo?: string;
  quem?: string;
  sistema?: string;
  como?: string;
  lt?: number | null;
  tt?: number | null;
  tp?: number | string | null;
  tpUnit?: string;
  entrada?: number | null;
  tipo_atividade?: string | null;
  percentCompleteAccurate?: number | null;
  pessoasSimultaneas?: number | null;
  p?: number | null;
  posicao?: { x: number; y: number };
  decisao_sim_pct?: number | null;
  decisao_nao_pct?: number | null;
  conexoes_saida?: Array<{ targetId: string; sourceHandle?: string }>;
}

export interface EnterpriseActionInput {
  id: string;
  title?: string;
  description?: string;
  status?: string;
  responsible?: string;
  responsavel?: string;
  deadline?: string;
  prazo?: string;
  projectId?: string;
  linkedNodeId?: string;
}

export interface EnterpriseProblemInput {
  id: string;
  title: string;
  status?: string;
  priority?: string;
}

export interface EnterpriseKaizenMetric {
  label: string;
  baseline: number | null;
  target: number | null;
  current: number | null;
  unit?: string;
}

export interface EnterpriseKaizenInput {
  id: string;
  title: string;
  status?: string;
  responsible?: string | null;
  metrics?: EnterpriseKaizenMetric[];
  annualCostSaved?: number | null;
}

export interface EnterpriseReportInput {
  processName: string;
  organizationName?: string | null;
  generatedAt?: Date;
  cards: EnterpriseCardInput[];
  actions?: EnterpriseActionInput[];
  problems?: EnterpriseProblemInput[];
  kaizens?: EnterpriseKaizenInput[];
  /** Só entra se foi informado. O relatório não calcula benefício a partir do lead time. */
  annualCostSaved?: number | null;
  /** Cenário proposto e desenho-alvo, quando já existirem no mapa. */
  kaizenCards?: EnterpriseCardInput[] | null;
  futureCards?: EnterpriseCardInput[] | null;
}

export interface DataQuality {
  hasLeadTimeData: boolean;
  hasProcessingData: boolean;
  hasWaitingData: boolean;
  hasValueClassification: boolean;
  hasReworkData: boolean;
  hasCapacityData: boolean;
  hasKaizenData: boolean;
  hasActionData: boolean;
  hasProblemData: boolean;
  hasDemandData: boolean;
}

export interface StageAnalysis {
  code: string;
  name: string;
  owner: string | null;
  processingMinutes: number | null;
  touchMinutes: number | null;
  waitMinutes: number | null;
  leadSharePercent: number | null;
  situation: 'crítica' | 'atenção' | null;
  route: 'principal' | 'condicional';
  frequencyLabel: string;
  permanenceLabel: string;
  participationLabel: string;
  signals: string[];
}

export interface FlowExceptionView {
  name: string;
  occurrenceLabel: string;
  permanenceLabel: string;
  impactLabel: string;
  nature: string;
  message: string;
}

export interface EvolutionView {
  indicator: string;
  current: string;
  kaizen: string;
  future: string;
  variation: string;
  kaizenNote: string | null;
}

export interface FlowPriority {
  rank: number;
  criterion: string;
  stageName: string | null;
  evidence: string;
}

export interface OpportunityItem {
  priority: string;
  opportunity: string;
  evidence: string;
  impact: string | null;
  effort: null;
  recommendation: string;
  kind: 'evidência' | 'investigação';
}

export interface EnterpriseReportModel {
  processName: string;
  organizationName: string | null;
  generatedAt: string;
  version: string;
  dataQuality: DataQuality;
  kpis: Array<{ label: string; value: string }>;
  unavailableNotes: string[];
  executiveReading: string[];
  priorities: FlowPriority[];
  stages: StageAnalysis[];
  composition: {
    totalLeadMinutes: number | null;
    totalTouchMinutes: number | null;
    totalWaitMinutes: number | null;
    pcePercent: number | null;
    processingShare: number | null;
    waitShare: number | null;
    expectedLeadMinutes: number | null;
    expectedPcePercent: number | null;
    expectedLead: { value: number | null; calculable: boolean; reason: string | null };
  };
    expectedBreakdown: Array<{ name: string; probability: string; permanence: string; contribution: string; contributionMinutes: number }>;
  pageOneRisks: FlowExceptionView[];
  executiveConclusion: string[];
  kaizenUnchanged: boolean;
  actionOrigins: Array<{ id: string; origin: string }>;
  exceptions: FlowExceptionView[];
  rankings: {
    mainPermanences: Array<{ name: string; label: string }>;
    mainWaits: Array<{ name: string; label: string }>;
    highImpact: Array<{ name: string; label: string }>;
  };
  evolution: EvolutionView[];
  structuralChanges: string[];
  stateSemantics: { current: string; kaizen: string; future: string };
  flow: {
    activities: number;
    decisions: number;
    returns: number | null;
    handoffs: number | null;
    owners: string[];
    manualCount: number | null;
    systemCount: number | null;
  };
  bottleneck: {
    determined: boolean;
    stageName: string | null;
    evidence: string;
  };
  rework: { informed: boolean; percent: number | null };
  opportunities: OpportunityItem[];
  problems: EnterpriseProblemInput[];
  actions: EnterpriseActionInput[];
  kaizens: EnterpriseKaizenInput[];
  financial: { annualCostSaved: number | null };
  methodology: string[];
}

const VERSION = 'Enterprise 4';

function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/** lt informado. Zero explícito permanece zero. Unidade segue convertLtToMinutes. */
export function readStageLeadMinutes(card: EnterpriseCardInput): number | null {
  const fromLt = finiteOrNull(card.lt);
  if (fromLt != null) {
    if (fromLt === 0) return 0;
    if (fromLt < 0) return null;
    return convertLtToMinutes(fromLt, card.tpUnit);
  }
  const fromTp = finiteOrNull(card.tp);
  if (fromTp == null || fromTp < 0) return null;
  if (fromTp === 0) return 0;
  return convertLtToMinutes(fromTp, card.tpUnit);
}

/** tt é o tempo de realização do motor, em minutos. */
export function readTouchMinutes(card: EnterpriseCardInput): number | null {
  const touch = finiteOrNull(card.tt);
  if (touch == null || touch < 0) return null;
  return touch;
}

export { formatExecutiveDuration } from './enterpriseFlowAnalysis';

export function formatMinutes(minutes: number | null): string | null {
  if (minutes == null) return null;
  return formatExecutiveDuration(minutes);
}

function activeCards(cards: EnterpriseCardInput[]): EnterpriseCardInput[] {
  return cards.filter((card) => card.status !== 'removido' && card.status !== 'deletado' && card.tipo !== 'anotacao');
}

function isActivity(card: EnterpriseCardInput): boolean {
  return card.tipo === 'atividade' || (!card.tipo && Boolean(card.atividade));
}

function stageName(card: EnterpriseCardInput, index: number): string {
  const name = card.atividade?.trim() || card.cliente?.trim();
  return name || `Etapa ${String(index + 1).padStart(2, '0')}`;
}

function ownerOf(card: EnterpriseCardInput): string | null {
  const owner = card.cargo?.trim() || card.responsavel?.trim() || card.quem?.trim();
  return owner || null;
}

function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

function percentPoint(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
}

export function buildExecutiveConclusion(input: {
  leadLabel: string | null;
  waitShare: number | null;
  waits: string[];
  highImpact: Array<{ name: string; occurrenceLabel: string; permanenceLabel: string }>;
  mainReduction: number | null;
  expectedReduction: number | null;
  pceFrom: number | null;
  pceTo: number | null;
}): string[] {
  const lines: string[] = [];
  if (input.leadLabel) {
    const share = input.waitShare == null ? '' : `, dos quais ${Math.round(input.waitShare)}% correspondem a espera`;
    lines.push(`O fluxo principal apresenta lead time de ${input.leadLabel}${share}.`);
  }
  if (input.waits.length > 0) {
    lines.push(`As esperas recorrentes de maior duração estão em ${joinNames(input.waits.slice(0, 3))}.`);
  }
  if (input.highImpact.length > 0) {
    const first = input.highImpact[0];
    const count = input.highImpact.length === 1 ? 'uma exceção de alto impacto' : `${input.highImpact.length} exceções de alto impacto`;
    lines.push(`O mapa registra ${count}: ${first.name}, com probabilidade de passagem de ${first.occurrenceLabel} e permanência de ${first.permanenceLabel} quando a rota ocorre.`);
  }
  const projected: string[] = [];
  if (input.mainReduction != null && input.mainReduction > 0) {
    projected.push(`redução de ${percentPoint(input.mainReduction)}% no lead time da rota principal`);
  }
  if (input.expectedReduction != null && input.expectedReduction > 0) {
    projected.push(`redução de ${percentPoint(input.expectedReduction)}% no lead time esperado`);
  }
  if (projected.length === 2 && projected[0].startsWith('redução de ') && projected[1].startsWith('redução de ')) {
    lines.push(`O estado futuro projeta ${projected[0]} e de ${projected[1].slice('redução de '.length)}.`);
  } else if (projected.length > 0) {
    lines.push(`O estado futuro projeta ${projected.join(' e ')}.`);
  }
  if (input.pceFrom != null && input.pceTo != null && input.pceFrom !== input.pceTo) {
    lines.push(`O PCE projetado passa de ${percentPoint(input.pceFrom)}% para ${percentPoint(input.pceTo)}%.`);
  }
  return lines.slice(0, 5);
}

export function formatPriorityLines(priority: { rank: number; criterion: string; stageName: string | null; evidence: string }): string[] {
  return [
    `P${priority.rank}`,
    priority.criterion,
    [priority.stageName, priority.evidence].filter(Boolean).join(' · '),
  ];
}

export function presentActionStatus(status?: string | null): string {
  const raw = (status ?? '').trim();
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  const known: Record<string, string> = {
    todo: 'A fazer',
    a_fazer: 'A fazer',
    in_progress: 'Em andamento',
    doing: 'Em andamento',
    completed: 'Concluída',
    done: 'Concluída',
    concluida: 'Concluída',
    blocked: 'Bloqueada',
    cancelled: 'Cancelada',
    canceled: 'Cancelada',
  };
  if (!raw) return '-';
  return known[key] ?? raw;
}

export function buildEnterpriseReport(input: EnterpriseReportInput): EnterpriseReportModel {
  const cards = activeCards(input.cards);
  const activities = cards.filter(isActivity);
  const decisions = cards.filter((card) => card.tipo === 'decisao');
  const generatedAt = (input.generatedAt ?? new Date()).toLocaleDateString('pt-BR');
  const flow = analyzeFlow(input.cards);
  const kaizenReady = scenarioIsComparable(input.cards, input.kaizenCards);
  const futureReady = scenarioIsComparable(input.cards, input.futureCards);
  const kaizenFlow = kaizenReady && input.kaizenCards ? analyzeFlow(input.kaizenCards) : null;
  const futureFlow = futureReady && input.futureCards ? analyzeFlow(input.futureCards) : null;

  const totalLeadMinutes = flow.mainLeadMinutes;
  const totalTouchMinutes = flow.mainTouchMinutes;
  const totalWaitMinutes = flow.mainWaitMinutes;
  const pcePercent = flow.mainPcePercent;
  const stages: StageAnalysis[] = flow.stages.map((stage) => ({
    code: stage.code,
    name: stage.name,
    owner: stage.owner,
    processingMinutes: stage.permanenceMinutes,
    touchMinutes: stage.touchMinutes,
    waitMinutes: stage.waitMinutes,
    leadSharePercent: stage.route === 'principal' && totalLeadMinutes != null && totalLeadMinutes > 0 && stage.permanenceMinutes != null
      ? (stage.permanenceMinutes / totalLeadMinutes) * 100
      : null,
    situation: null,
    route: stage.route,
    frequencyLabel: stage.frequencyLabel,
    permanenceLabel: stage.permanenceMinutes == null ? '-' : stage.informedLabel,
    participationLabel: stage.participationLabel,
    signals: stage.signals,
  }));
  const displayStages = stages;

  const reworkValues = activities
    .map((card) => finiteOrNull(card.percentCompleteAccurate))
    .filter((value): value is number => value != null);
  const reworkPercent = reworkValues.length > 0
    ? reworkValues.reduce((sum, value) => sum + (100 - value), 0) / reworkValues.length
    : null;

  const demandCard = cards.find((card) => card.tipo === 'card_inicial');
  const demand = demandCard ? finiteOrNull(demandCard.entrada) : null;

  const owners = flow.owners;
  const handoffs = flow.handoffs;
  const returns = flow.returns;
  const manualCount = activities.some((card) => card.como?.trim())
    ? activities.filter((card) => /manual/i.test(card.como ?? '')).length
    : null;
  const systemCount = activities.some((card) => card.sistema?.trim())
    ? activities.filter((card) => Boolean(card.sistema?.trim())).length
    : null;
  const hasValueClassification = activities.some((card) => card.tipo_atividade === 'VA' || card.tipo_atividade === 'NVA');

  const priorities: FlowPriority[] = [];
  const waitRanking = flow.mainWaits;
  if (waitRanking[0]) {
    priorities.push({
      rank: 1,
      criterion: 'Espera relevante da rota principal',
      stageName: waitRanking[0].name,
      evidence: formatMinutes(waitRanking[0].minutes) ?? '',
    });
  }
  if (waitRanking[1]) {
    priorities.push({
      rank: priorities.length + 1,
      criterion: 'Segunda maior espera da rota principal',
      stageName: waitRanking[1].name,
      evidence: formatMinutes(waitRanking[1].minutes) ?? '',
    });
  }
  const highImpact = flow.exceptions.find((item) => item.highImpact);
  if (highImpact && priorities.length < 3) {
    priorities.push({
      rank: priorities.length + 1,
      criterion: 'Exceção de alto impacto',
      stageName: highImpact.name,
      evidence: `Ocorrência ${highImpact.occurrenceLabel}. Permanência quando ocorre: ${highImpact.permanenceLabel}.`,
    });
  }

  const kpis: Array<{ label: string; value: string }> = [];
  if (demand != null) kpis.push({ label: 'Demanda', value: `${demand.toLocaleString('pt-BR')}` });
  const timedStages = flow.stages.filter((stage) => stage.permanenceMinutes != null);
  const expectedLead = timedStages.length === 0
    ? { value: null, calculable: false, reason: null }
    : timedStages.some((stage) => stage.frequency == null)
      ? { value: null, calculable: false, reason: 'Probabilidade insuficiente para calcular.' }
      : { value: flow.expectedLeadMinutes, calculable: flow.expectedLeadMinutes != null, reason: flow.expectedLeadMinutes == null ? 'Probabilidade insuficiente para calcular.' : null };
  if (totalLeadMinutes != null) kpis.push({ label: 'Lead time da rota principal', value: formatMinutes(totalLeadMinutes) ?? '' });
  if (expectedLead.reason || expectedLead.calculable) {
    kpis.push({ label: 'Lead time esperado', value: expectedLead.calculable ? (formatMinutes(expectedLead.value) ?? '-') : '-' });
  }
  if (totalTouchMinutes != null) kpis.push({ label: 'Realização', value: formatMinutes(totalTouchMinutes) ?? '' });
  if (totalWaitMinutes != null) kpis.push({ label: 'Espera', value: formatMinutes(totalWaitMinutes) ?? '' });
  if (pcePercent != null) kpis.push({ label: 'PCE da rota principal', value: `${pcePercent.toFixed(1).replace('.', ',')}%` });
  kpis.push({ label: 'Etapas', value: String(activities.length) });
  kpis.push({ label: 'Decisões', value: String(decisions.length) });
  if (flow.largestMainName) kpis.push({ label: 'Maior permanência', value: flow.largestMainName });
  if (flow.highImpactCount > 0) kpis.push({ label: 'Exceções de alto impacto', value: String(flow.highImpactCount) });
  if (reworkPercent != null) kpis.push({ label: 'Retrabalho', value: `${reworkPercent.toFixed(1).replace('.', ',')}%` });

  const unavailableNotes: string[] = [];
  if (totalLeadMinutes == null) unavailableNotes.push('Lead time da rota principal não calculável com os dados disponíveis.');
  if (pcePercent == null) unavailableNotes.push('PCE da rota principal não calculável com os dados disponíveis.');
  if (expectedLead.reason) unavailableNotes.push(expectedLead.reason);
  if (flow.probabilityMissing) unavailableNotes.push('Há rota condicional sem probabilidade informada. Essa probabilidade não foi assumida.');
  unavailableNotes.push('O mapa não possui faixa de mínimo e máximo. A permanência exibida é o tempo informado da etapa.');
  unavailableNotes.push('Gargalo não determinado: não há evidência de restrição de capacidade.');
  if (reworkPercent == null) unavailableNotes.push('Retrabalho não informado.');

  const reading: string[] = [];
  if (totalLeadMinutes != null) {
    reading.push(`A rota principal tem lead time de ${formatMinutes(totalLeadMinutes)}.`);
  } else {
    reading.push('A rota principal ainda não tem lead time calculável.');
  }
  if (waitRanking.length > 0) {
    const named = waitRanking.slice(0, 3).map((item) => `${item.name} (${formatMinutes(item.minutes)})`);
    reading.push(`As esperas mais relevantes da rota principal são ${joinNames(named)}.`);
  }
  if (kaizenFlow) reading.push('O cenário Kaizen está descrito como proposta.');
  if (futureFlow) reading.push('O estado futuro está descrito como desenho-alvo.');

  const opportunities: OpportunityItem[] = priorities.slice(0, 3).map((priority) => ({
    priority: `P${priority.rank}`,
    opportunity: priority.stageName
      ? `${priority.criterion}: ${priority.stageName}`
      : priority.criterion,
    evidence: priority.evidence,
    impact: priority.evidence,
    effort: null,
    recommendation: priority.criterion === 'Exceção de alto impacto'
      ? 'Confirmar com a operação em quais pedidos essa rota ocorre, antes de definir contramedida.'
      : 'Observar a espera desta etapa no Gemba antes de definir contramedida.',
    kind: 'investigação',
  }));

  const annual = finiteOrNull(input.annualCostSaved);
  const kaizens = input.kaizens ?? [];
  const kaizenAnnual = kaizens
    .map((item) => finiteOrNull(item.annualCostSaved))
    .filter((value): value is number => value != null);
  const financialAnnual = annual != null
    ? annual
    : kaizenAnnual.length > 0
      ? kaizenAnnual.reduce((sum, value) => sum + value, 0)
      : null;

  const actions = input.actions ?? [];
  const problems = input.problems ?? [];
  const evolution = evolutionRows(flow, kaizenFlow, futureFlow, formatMinutes);
  const structural = [
    ...(kaizenFlow && input.kaizenCards ? structuralChanges(input.cards, input.kaizenCards, 'cenário Kaizen') : []),
    ...(futureFlow && input.futureCards ? structuralChanges(input.cards, input.futureCards, 'estado futuro') : []),
  ];
  const pageOneRisks = flow.exceptions.filter((item) => item.highImpact);
  const drop = (current: number | null, next: number | null) =>
    current != null && next != null && current > 0 && next < current ? ((current - next) / current) * 100 : null;
  const executiveConclusion = buildExecutiveConclusion({
    leadLabel: formatMinutes(totalLeadMinutes),
    waitShare: pcePercent != null ? Math.max(0, 100 - pcePercent) : null,
    waits: flow.mainWaits.map((item) => item.name),
    highImpact: pageOneRisks,
    mainReduction: futureFlow ? drop(flow.mainLeadMinutes, futureFlow.mainLeadMinutes) : null,
    expectedReduction: futureFlow ? drop(flow.expectedLeadMinutes, futureFlow.expectedLeadMinutes) : null,
    pceFrom: futureFlow && flow.mainPcePercent != null && futureFlow.mainPcePercent != null ? flow.mainPcePercent : null,
    pceTo: futureFlow && flow.mainPcePercent != null && futureFlow.mainPcePercent != null ? futureFlow.mainPcePercent : null,
  });

  return {
    processName: input.processName?.trim() || 'Processo sem nome',
    organizationName: input.organizationName?.trim() || null,
    generatedAt,
    version: VERSION,
    dataQuality: {
      hasLeadTimeData: totalLeadMinutes != null,
      hasProcessingData: totalTouchMinutes != null,
      hasWaitingData: totalWaitMinutes != null,
      hasValueClassification,
      hasReworkData: reworkPercent != null,
      hasCapacityData: false,
      hasKaizenData: kaizens.length > 0,
      hasActionData: actions.length > 0,
      hasProblemData: problems.length > 0,
      hasDemandData: demand != null,
    },
    kpis,
    unavailableNotes,
    executiveReading: reading.slice(0, 6),
    priorities: priorities.slice(0, 3),
    stages: displayStages,
    composition: {
      totalLeadMinutes,
      totalTouchMinutes,
      totalWaitMinutes,
      pcePercent,
      processingShare: pcePercent,
      waitShare: pcePercent != null ? Math.max(0, 100 - pcePercent) : null,
      expectedLeadMinutes: expectedLead.calculable ? expectedLead.value : null,
      expectedPcePercent: expectedLead.calculable ? flow.expectedPcePercent : null,
      expectedLead,
    },
    expectedBreakdown: expectedLead.calculable
      ? timedStages.map((stage) => {
          const contributionMinutes = (stage.frequency ?? 0) * (stage.permanenceMinutes ?? 0);
          return {
            name: stage.name,
            probability: stage.frequencyLabel,
            permanence: stage.informedLabel,
            contribution: formatMinutes(contributionMinutes) ?? '-',
            contributionMinutes,
          };
        })
      : [],
    pageOneRisks: pageOneRisks.map((item) => ({
      name: item.name,
      occurrenceLabel: item.occurrenceLabel,
      permanenceLabel: item.permanenceLabel,
      impactLabel: item.impactLabel,
      nature: item.nature,
      message: item.message,
    })),
    executiveConclusion,
    kaizenUnchanged: Boolean(kaizenFlow) && evolution.length > 0 && evolution.every((row) => row.kaizenNote != null),
    actionOrigins: actions.map((action) => ({
      id: action.id,
      origin: action.linkedNodeId
        ? (flow.stages.find((stage) => stage.id === action.linkedNodeId)?.name ?? '-')
        : '-',
    })),
    exceptions: flow.exceptions.map((item) => ({
      name: item.name,
      occurrenceLabel: item.occurrenceLabel,
      permanenceLabel: item.permanenceLabel,
      impactLabel: item.impactLabel,
      nature: item.nature,
      message: item.message,
    })),
    rankings: {
      mainPermanences: flow.mainPermanences.map((item) => ({
        name: item.name,
        label: flow.stages.find((stage) => stage.name === item.name)?.informedLabel ?? formatMinutes(item.minutes) ?? '-',
      })),
      mainWaits: flow.mainWaits.map((item) => ({ name: item.name, label: formatMinutes(item.minutes) ?? '-' })),
      highImpact: flow.exceptions.filter((item) => item.highImpact).map((item) => ({ name: item.name, label: `${item.occurrenceLabel} · ${item.permanenceLabel}` })),
    },
    evolution,
    structuralChanges: structural,
    stateSemantics: { current: 'ATUAL', kaizen: 'PROPOSTO', future: 'FUTURO/ALVO' },
    flow: {
      activities: activities.length,
      decisions: decisions.length,
      returns,
      handoffs,
      owners,
      manualCount,
      systemCount,
    },
    bottleneck: {
      determined: false,
      stageName: null,
      evidence: flow.largestMainName
        ? `Maior permanência da rota principal: ${flow.largestMainName}. Gargalo não determinado. Este relatório não possui evidência de restrição de capacidade.`
        : 'Gargalo não determinado. Este relatório não possui evidência de restrição de capacidade.',
    },
    rework: { informed: reworkPercent != null, percent: reworkPercent },
    opportunities,
    problems,
    actions,
    kaizens,
    financial: { annualCostSaved: financialAnnual },
    methodology: [
      'Rota principal: caminho que segue, em cada decisão, a saída de maior probabilidade da saída. Em empate, segue a saída Sim.',
      'Rota condicional: caminho da outra saída da decisão. A permanência vale quando essa rota ocorre.',
      'Probabilidade da saída: percentual cadastrado naquela decisão. Probabilidade ausente não é substituída por 100% nem por 50%.',
      'Probabilidade de passagem: chance acumulada de um pedido chegar à etapa. Exemplo: 95% na primeira decisão e 98% na seguinte resultam em 93,1%. Em decisões seguidas, as probabilidades da saída se multiplicam. Quando dois caminhos se reencontram, as chances se somam e a etapa entra uma única vez.',
      'Permanência: tempo informado da etapa. O mapa não guarda mínimo, máximo e típico, então uma faixa não vira média.',
      'Espera: permanência menos realização, somente quando os dois tempos foram informados.',
      'Lead time da rota principal: soma das permanências das etapas dessa rota, cada uma uma vez. O retorno não soma o tempo de novo.',
      'Lead time esperado: soma de permanência vezes probabilidade de passagem. Só é calculado quando toda etapa com tempo tem essa probabilidade. Probabilidade ausente não é substituída.',
      'Um retorno não soma de novo a permanência da etapa. Sem o número de repetições do retorno, o relatório não estima quantas vezes ele ocorre.',
      'PCE da rota principal: realização da rota principal dividida pelo lead time dessa rota. Uma rota condicional não entra como se todos os pedidos passassem por ela.',
      'Maior permanência: etapa da rota principal com o maior tempo quando ocorre. Isso não é chamado de gargalo.',
      'Exceção de alto impacto: rota condicional cuja probabilidade da saída é no máximo a metade da saída principal, com permanência maior que qualquer etapa da rota principal.',
      'Atual é a condição mapeada. Cenário Kaizen é proposta. Estado futuro é desenho-alvo.',
      'A comparação só inclui indicador calculável em todos os estados presentes. Mudança estrutural só aparece quando a mesma etapa é reconhecida nos dois estados.',
      'Valor anual só entra quando foi informado. O relatório não converte tempo em dinheiro.',
    ],
  };
}

export function stageTableRows(model: EnterpriseReportModel): string[][] {
  return model.stages.map((stage) => [
    stage.code,
    stage.name,
    stage.owner ? presentOwners([stage.owner]).join(' · ') : '-',
    stage.frequencyLabel,
    stage.permanenceLabel,
    formatMinutes(stage.touchMinutes) ?? '-',
    formatMinutes(stage.waitMinutes) ?? '-',
    stage.participationLabel,
    stage.signals.join(', '),
  ]);
}

export function reportContainsInternalId(model: EnterpriseReportModel, ids: string[]): boolean {
  const blob = JSON.stringify({
    kpis: model.kpis,
    stages: model.stages,
    priorities: model.priorities,
    reading: model.executiveReading,
    opportunities: model.opportunities,
  });
  return ids.some((id) => id.length > 4 && blob.includes(id));
}
